<script>
    import Icon from '../icons/Icon.svelte';

    export let pixKey = '';
    export let pixKeyType = 'cnpj';
    export let pixName = '';
    export let acceptPix = true;
    export let acceptCard = true;
    export let acceptCash = true;
    export let mpStatus = { isConnected: false, userId: null, connectedAt: null };
    export let isLoadingMp = false;
    export let isConnectingMp = false;
    export let isDisconnectingMp = false;
    export let onConnectMp = () => {};
    export let onDisconnectMp = () => {};
</script>

<div class="tab-pane">
    <!-- 1. Recebimento Instantâneo via PIX (Direto na Conta do Restaurante) -->
    <section class="settings-card">
        <div class="card-header">
            <div class="card-title-group">
                <Icon name="dollar-sign" size={18} />
                <h4>Recebimento via PIX da Loja</h4>
            </div>
            <span class="card-subtitle">Cadastre a chave PIX onde você deseja receber o dinheiro dos pedidos dos seus clientes.</span>
        </div>

        <div class="toggle-row-standalone">
            <label class="switch-control">
                <input type="checkbox" bind:checked={acceptPix} />
                <span class="switch-label">
                    <strong>Habilitar Pagamentos via PIX</strong>
                    <small>Se ativado, os clientes poderão pagar via QR Code / Copia e Cola no WhatsApp e no Cardápio.</small>
                </span>
            </label>
        </div>

        {#if acceptPix}
            <div class="form-grid">
                <div class="form-group">
                    <label for="pix-type">Tipo de Chave PIX</label>
                    <select id="pix-type" bind:value={pixKeyType} class="form-select">
                        <option value="cnpj">CNPJ</option>
                        <option value="cpf">CPF</option>
                        <option value="phone">Celular (com DDD)</option>
                        <option value="email">E-mail</option>
                        <option value="random">Chave Aleatória (EVP)</option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="pix-key">Chave PIX do Estabelecimento *</label>
                    <input
                        id="pix-key"
                        type="text"
                        bind:value={pixKey}
                        placeholder={pixKeyType === 'cnpj' ? '00.000.000/0001-00' : pixKeyType === 'cpf' ? '000.000.000-00' : pixKeyType === 'phone' ? '(11) 99999-9999' : 'suachave@exemplo.com'}
                        class="form-input"
                    />
                </div>

                <div class="form-group full-width">
                    <label for="pix-name">Nome do Titular / Razão Social na Conta PIX</label>
                    <input
                        id="pix-name"
                        type="text"
                        bind:value={pixName}
                        placeholder="Ex: Pizzaria Forno Nobre Ltda"
                        class="form-input"
                    />
                    <small class="input-hint">Esse nome é exibido para o cliente no comprovante para ele ter certeza de que está pagando para a loja certa.</small>
                </div>
            </div>
        {/if}
    </section>

    <!-- 2. Integração Mercado Pago Connect (Baixa Automática) -->
    <section class="settings-card mp-card {mpStatus.isConnected ? 'mp-connected' : ''}">
        <div class="mp-header">
            <div class="mp-icon-box">
                <Icon name="wallet" size={24} />
            </div>
            <div class="mp-info">
                <div class="mp-badge-row">
                    <span class="mp-title">Mercado Pago Connect</span>
                    {#if mpStatus.isConnected}
                        <span class="status-badge connected">
                            <Icon name="check-circle" size={12} />
                            CONECTADO
                        </span>
                    {:else}
                        <span class="status-badge pending">NÃO CONECTADO</span>
                    {/if}
                </div>
                <p>
                    {#if mpStatus.isConnected}
                        Sua conta Mercado Pago está vinculada com sucesso. Quando o cliente paga via PIX no Cardápio, o pedido tem <strong>baixa automática instantânea</strong> sem necessidade de conferência manual.
                        {#if mpStatus.connectedAt}
                            <small class="connected-date">Conectado em: {new Date(mpStatus.connectedAt).toLocaleDateString('pt-BR')}</small>
                        {/if}
                    {:else}
                        Conecte sua conta do Mercado Pago para gerar QR Codes dinâmicos com confirmação automática de pagamento direto no seu painel.
                    {/if}
                </p>
            </div>

            <div class="mp-action">
                {#if mpStatus.isConnected}
                    <button
                        type="button"
                        class="btn-mp-disconnect"
                        on:click={onDisconnectMp}
                        disabled={isDisconnectingMp}
                    >
                        <span>{isDisconnectingMp ? 'Desconectando...' : 'Desconectar Conta'}</span>
                    </button>
                {:else}
                    <button
                        type="button"
                        class="btn-mp-connect"
                        on:click={onConnectMp}
                        disabled={isConnectingMp || isLoadingMp}
                    >
                        <Icon name="zap" size={14} />
                        <span>{isConnectingMp ? 'Aguardando login...' : 'Conectar em 1 Clique'}</span>
                    </button>
                {/if}
            </div>
        </div>
    </section>

    <!-- 3. Formas de Pagamento Presencial / na Entrega -->
    <section class="settings-card">
        <div class="card-header">
            <div class="card-title-group">
                <Icon name="credit-card" size={18} />
                <h4>Pagamentos no Local & na Entrega</h4>
            </div>
            <span class="card-subtitle">Formas físicas de recebimento quando o entregador chega ou o cliente está no balcão.</span>
        </div>

        <div class="payment-methods-grid">
            <label class="method-card {acceptCash ? 'active' : ''}">
                <div class="method-icon-box">
                    <Icon name="dollar-sign" size={20} />
                </div>
                <div class="method-content">
                    <span class="method-title">Dinheiro Físico</span>
                    <span class="method-desc">Permite ao cliente solicitar troco no checkout do cardápio e WhatsApp.</span>
                </div>
                <input type="checkbox" bind:checked={acceptCash} class="toggle-checkbox" />
            </label>

            <label class="method-card {acceptCard ? 'active' : ''}">
                <div class="method-icon-box">
                    <Icon name="credit-card" size={20} />
                </div>
                <div class="method-content">
                    <span class="method-title">Cartão na Maquininha</span>
                    <span class="method-desc">O motoboy leva a máquina de cartão de crédito/débito até o endereço.</span>
                </div>
                <input type="checkbox" bind:checked={acceptCard} class="toggle-checkbox" />
            </label>
        </div>
    </section>
</div>

<style>
    .tab-pane {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .settings-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        padding: 22px 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    .card-header {
        margin-bottom: 16px;
    }

    .card-title-group {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #0F172A;
    }

    .card-title-group h4 {
        font-size: 0.98rem;
        font-weight: 700;
        margin: 0;
        color: #0F172A;
    }

    .card-subtitle {
        font-size: 0.82rem;
        color: #64748B;
        display: block;
        margin-top: 4px;
    }

    .toggle-row-standalone {
        margin-bottom: 16px;
    }

    .switch-control {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        cursor: pointer;
    }

    .switch-control input {
        margin-top: 3px;
        accent-color: #FF6A00;
        width: 16px;
        height: 16px;
    }

    .switch-label strong {
        font-size: 0.88rem;
        color: #0F172A;
        display: block;
    }

    .switch-label small {
        font-size: 0.76rem;
        color: #64748B;
    }

    .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 14px;
        padding-top: 14px;
        border-top: 1px solid #F1F5F9;
    }

    .full-width {
        grid-column: 1 / -1;
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .form-group label {
        font-size: 0.78rem;
        font-weight: 600;
        color: #475569;
    }

    .form-input, .form-select {
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 8px;
        padding: 10px 14px;
        color: #0F172A;
        font-size: 0.86rem;
        transition: all 0.2s ease;
    }

    .form-input:focus, .form-select:focus {
        outline: none;
        border-color: #FF6A00;
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.12);
    }

    .input-hint {
        font-size: 0.74rem;
        color: #64748B;
    }

    .mp-card {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-left: 4px solid #3B82F6;
    }

    .mp-card.mp-connected {
        background: #F0FDF4;
        border: 1px solid #BBF7D0;
        border-left: 4px solid #16A34A;
    }

    .mp-header {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }

    .mp-icon-box {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        background: #EFF6FF;
        color: #2563EB;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .mp-card.mp-connected .mp-icon-box {
        background: #DCFCE7;
        color: #16A34A;
    }

    .mp-info {
        flex: 1;
        min-width: 220px;
    }

    .mp-badge-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 4px;
    }

    .mp-title {
        font-size: 1rem;
        font-weight: 700;
        color: #0F172A;
    }

    .status-badge {
        font-size: 0.68rem;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .status-badge.connected {
        background: #DCFCE7;
        color: #166534;
        border: 1px solid #BBF7D0;
    }

    .status-badge.pending {
        background: #F1F5F9;
        color: #64748B;
        border: 1px solid #E2E8F0;
    }

    .mp-info p {
        font-size: 0.82rem;
        color: #475569;
        margin: 0;
        line-height: 1.45;
    }

    .connected-date {
        display: block;
        margin-top: 4px;
        color: #64748B;
    }

    .btn-mp-connect {
        background: #0284c7;
        border: none;
        color: #fff;
        padding: 9px 16px;
        border-radius: 8px;
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s;
    }

    .btn-mp-connect:hover {
        background: #0369a1;
        box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
    }

    .btn-mp-disconnect {
        background: #FFFFFF;
        border: 1px solid #FCA5A5;
        color: #DC2626;
        padding: 8px 14px;
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
    }

    .btn-mp-disconnect:hover {
        background: #FEF2F2;
    }

    .payment-methods-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 14px;
    }

    .method-card {
        display: flex;
        align-items: center;
        gap: 14px;
        background: #FFFFFF;
        border: 1.5px solid #E2E8F0;
        border-radius: 10px;
        padding: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .method-card:hover {
        border-color: #CBD5E1;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
    }

    .method-card.active {
        border-color: #FF6A00;
        background: #FFF7ED;
    }

    .method-icon-box {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        background: #F1F5F9;
        color: #64748B;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .method-card.active .method-icon-box {
        background: #FFEDD5;
        color: #EA580C;
    }

    .method-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .method-title {
        font-size: 0.88rem;
        font-weight: 700;
        color: #0F172A;
    }

    .method-desc {
        font-size: 0.74rem;
        color: #64748B;
        line-height: 1.3;
    }

    .toggle-checkbox {
        width: 18px;
        height: 18px;
        accent-color: #FF6A00;
        cursor: pointer;
    }
</style>
